import React, { useState } from "react";
import { Constants } from "@eagerdog/constants";
import { IMoveUpSummary, IEventSummary } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";

import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import { toast } from "src/components/Toast/ToastManager";

import type { CustomCellRendererProps } from "ag-grid-react";

interface CustomMoveUpCellRendererProps extends CustomCellRendererProps {
  value: {
    moveUp: IMoveUpSummary;
    event: IEventSummary;
    getMoveUps: () => void;
  };
}

export const ActionsRenderer: React.FC<CustomMoveUpCellRendererProps> = (props) => {
  const { isShown, toggle } = useModal();
  const [isReversing, setIsReversing] = useState(false);

  const { moveUp, event, getMoveUps } = props.value ?? {};

  if (!moveUp) {
    return null;
  }

  const handleReverse = async () => {
    setIsReversing(true);
    try {
      await apiService.reverseMoveUp(event._id, moveUp._id, "Reversed from move-ups table");
      toast.show({
        title: "Move-Up Reversed",
        content: `Successfully reversed move-up for ${moveUp.call_name || "dog"}. Dog has been returned to ${moveUp.from_element} / ${moveUp.from_level}${moveUp.from_section ? ` (${moveUp.from_section})` : ""}.`,
        duration: 10000,
        type: "success",
      });
      getMoveUps();
    } catch (e: any) {
      toast.show({
        title: "Reverse Move-Up Failed",
        content: e.response?.data?.message || "Failed to reverse move-up. Please try again.",
        duration: 10000,
        errorDetails: e,
        type: "fail",
      });
    } finally {
      setIsReversing(false);
      toggle();
    }
  };

  // Only show actions for active move-ups
  if (moveUp.status !== Constants.move_up_status.active) {
    return null;
  }

  const menuOptions: IContextMenuOption[] = [
    {
      label: "Reverse Move-Up",
      onClick: () => {
        toggle();
      },
    },
  ];

  return (
    <div>
      <ContextMenu options={menuOptions} />
      <Modal
        className="Confirmation"
        isShown={isShown}
        hide={() => {
          if (!isReversing) toggle();
        }}
        modalContent={
          <Confirm
            message={
              <>
                <p>
                  <strong>Reverse Move-Up</strong>
                </p>
                <p>
                  Are you sure you want to reverse this move-up for{" "}
                  <strong>{moveUp.call_name || "this dog"}</strong>?
                </p>
                <p>
                  The dog will be returned from <strong>{moveUp.to_element} / {moveUp.to_level}</strong> back to{" "}
                  <strong>{moveUp.from_element} / {moveUp.from_level}</strong>.
                </p>
                <p style={{ color: "#d32f2f", marginTop: "10px" }}>
                  ⚠️ If results have been entered at the current level, they will be cleared.
                </p>
              </>
            }
            onConfirm={handleReverse}
            onCancel={() => {
              toggle();
            }}
            buttonsDisabled={isReversing}
          />
        }
      />
    </div>
  );
};
